import api from "@/api/api"
import { mutation, query } from "../../api/queries"
import { updateTree } from "@/utils/treeUtils"

export default {
	async resetBenefitGraph({ commit }) {
		commit("putBenefitGraph", [])
	},

	async fetchBenefitGraph({ commit }, { productId, locale }) {
		const { plan, type, version } = productId

		const res = await api({
			query: query.benefitGraph,
			variables: { where: { productId: { plan, type, version } } },
			locale
		})

		// Make a few adjustments:
		// Add parentTypeId into benefitGraph for convenience
		// If benefit has optionKey and doesn't have any options, it means this benefit is optional and we have to put benefit itself as an option so that it is displayed correclty
		const graph = updateTree({
			tree: res?.data?.products?.list?.[0]?.benefitGraph ?? [],
			updateFunction(node, parent) {
				// Add parentTypeId
				node.parentTypeId = parent?.typeId

				// Add benefit itself as an option
				if (node.optionKey && !node?.options?.length) node.options.push(node)
			}
		})

		commit("putBenefitGraph", graph)
		return Promise.resolve(res)
	},

	async addBenefit(_, payload) {
		const { productId, typeId, parentTypeId, optionKey, isValueInput } = payload

		const res = await api({
			query: mutation.addBenefit,
			variables: {
				productId,
				input: {
					typeId,
					parentTypeId,
					optionKey,
					isValueInput,
					value: { stringValue: "" }
				}
			}
		})

		// Check error
		if (res?.data?.addBenefit?.status !== "success") return Promise.resolve(res)
		return Promise.resolve(res)
	},

	async updateBenefit(_, payload) {
		const {
			typeId,
			currencyCode,
			productId,
			rawData,
			locale,
			name,
			description,
			value,
			detailedValue,
			optionKey,
			parentOptionKeys
		} = payload

		const { version = "", type = "", plan = "" } = productId
		const nameKey = `products-${plan}|${version}|${type}-benefits-${typeId}-name`
		const descriptionKey = `products-${plan}|${version}|${type}-benefits-${typeId}-description`
		let valueKey = `products-${plan}|${version}|${type}-benefits-${typeId}-format`
		let detailedValueKey = `products-${plan}|${version}|${type}-benefits-${typeId}-detailedFormat`

		// If there is optionKey, benefits is an option and we use different localization keys to localize it
		if (optionKey) {
			valueKey = `products-${plan}|${version}|${type}-benefits-${typeId}-options-${optionKey}-format`
			detailedValueKey = `products-${plan}|${version}|${type}-benefits-${typeId}-options-${optionKey}-detailedFormat`
		}

		// 1: Update benefit
		const res = await api({
			query: mutation.updateBenefit,
			variables: {
				productId,
				typeId,
				optionKey,
				input: {
					currencyCode,
					valueJsonString: rawData,
					parentOptionKeys
				}
			}
		})

		if (res?.data?.updateBenefit?.status !== "success")
			return Promise.resolve({ isSuccess: false, error: res?.data?.updateBenefit?.errors?.[0] || res?.errors?.[0]?.message })

		// 2: Update localization
		// Include only fields that are not null
		const localizationCalls = []

		if (name !== null && name !== undefined)
			localizationCalls.push(
				api({ locale, query: mutation.upsertL10n, variables: { l10n: { locale, key: nameKey, value: name } } })
			)

		if (description !== null && description !== undefined)
			localizationCalls.push(
				api({ locale, query: mutation.upsertL10n, variables: { l10n: { locale, key: descriptionKey, value: description } } })
			)

		if (value !== null && value !== undefined)
			localizationCalls.push(
				api({ locale, query: mutation.upsertL10n, variables: { l10n: { locale, key: valueKey, value: value } } })
			)

		if (detailedValue !== null && detailedValue !== undefined)
			localizationCalls.push(
				api({ locale, query: mutation.upsertL10n, variables: { l10n: { locale, key: detailedValueKey, value: detailedValue } } })
			)

		const localizationResults = await Promise.all(localizationCalls)

		// Check errors
		const failedLocalizationResults = localizationResults?.filter(item => item?.data?.upsertL10n?.status !== "success") ?? []
		if (failedLocalizationResults?.length)
			return Promise.resolve({ isSuccess: false, error: failedLocalizationResults?.[0]?.data?.upsertL10n?.errors?.[0] })

		return Promise.resolve({ isSuccess: true })
	},

	async updateL10nBenefitJson(_, payload) {
		const { typeId, productId, l10nBenefits, optionKey } = payload

		const { version, type, plan } = productId
		const nameKey = `products-${plan}|${version}|${type}-benefits-${typeId}-name`
		const descriptionKey = `products-${plan}|${version}|${type}-benefits-${typeId}-description`
		let valueKey = `products-${plan}|${version}|${type}-benefits-${typeId}-format`
		let detailedValueKey = `products-${plan}|${version}|${type}-benefits-${typeId}-detailedFormat`

		// If there is optionKey, benefits is an option and we use different localization keys to localize it
		if (optionKey) {
			valueKey = `products-${plan}|${version}|${type}-benefits-${typeId}-options-${optionKey}-format`
			detailedValueKey = `products-${plan}|${version}|${type}-benefits-${typeId}-options-${optionKey}-detailedFormat`
		}

		// 2: Update localization
		// Include only fields that are not null
		const parsedL10n = JSON.parse(l10nBenefits)
		const localizationCalls = []
		for (const [lang, data] of Object.entries(parsedL10n)) {
			const { name, description, value, detailedValue } = data
			if (name !== null && name !== undefined)
				localizationCalls.push(
					api({ locale: lang, query: mutation.upsertL10n, variables: { l10n: { locale: lang, key: nameKey, value: name } } })
				)

			if (description !== null && description !== undefined)
				localizationCalls.push(
					api({
						locale: lang,
						query: mutation.upsertL10n,
						variables: { l10n: { locale: lang, key: descriptionKey, value: description } }
					})
				)

			if (value !== null && value !== undefined)
				localizationCalls.push(
					api({ locale: lang, query: mutation.upsertL10n, variables: { l10n: { locale: lang, key: valueKey, value: value } } })
				)

			if (detailedValue !== null && detailedValue !== undefined)
				localizationCalls.push(
					api({
						locale: lang,
						query: mutation.upsertL10n,
						variables: { l10n: { locale: lang, key: detailedValueKey, value: detailedValue } }
					})
				)
		}

		const localizationResults = await Promise.all(localizationCalls)

		// Check errors
		const failedLocalizationResults = localizationResults?.filter(item => item?.data?.upsertL10n?.status !== "success") ?? []
		if (failedLocalizationResults?.length)
			return Promise.resolve({ isSuccess: false, error: failedLocalizationResults?.[0]?.data?.upsertL10n?.errors?.[0] })

		return Promise.resolve({ isSuccess: true })
	},

	async removeBenefit(_, { productId, typeId, optionKey }) {
		const res = await api({
			query: mutation.removeBenefit,
			variables: { productId, typeId, optionKey }
		})

		// Check error
		if (res?.data?.removeBenefit?.status !== "success") return Promise.resolve(res)

		return Promise.resolve(res)
	}
}
